import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationFacade, NotificationFacade, PurchaseService } from '@ridango/components';
import {
    CategoryProduct,
    PeriodType,
    Product,
    ProductCode,
    ProductGroup,
    ZoneInterface
} from '@ridango/components/lib/purchase/purchase.model';
import { AppStartupService, BaseComponent, TranslateObjectFieldPipe, WmbApiErrorResponse } from '@ridango/core';
import { takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { ModalController } from '@ionic/angular';
import { TicketWarningModalComponent } from '../ticket-warning-modal/ticket-warning-modal.component';
import { TicketDurationDescriptionService } from '@ridango/core/lib/services/ticket-duration-description.service';
import { MoneyFormatPipe } from '@ridango/core/lib/pipes/money-format.pipe';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'rwidget-purchase-ticket-category-product',
    templateUrl: './category-product.component.html',
    styleUrls: ['./category-product.component.scss'],
    providers: [MoneyFormatPipe, TranslateObjectFieldPipe]
})
export class CategoryProductComponent extends BaseComponent implements OnInit, OnDestroy {
    protected readonly ProductCode = ProductCode;
    @Output() nextStep: EventEmitter<void> = new EventEmitter<void>();
    @Input() categoryProducts: CategoryProduct[] = [];
    @Input() urls: string[];
    @Input() duration?: number;
    @Input() zone: ZoneInterface;
    @Input() showZone = true;
    @Input() zones: number;

    public assetsPath$ = this.applicationFacade.assetsPath$;
    public isLoading$ = this.purchaseService.isPurchaseDataLoading$;

    public productTypeSelectionsIsLoading = false;

    public productsGroups: ProductGroup[];
    public selectedProductId: number | string;
    public currentLanguage = this.translate.currentLang;
    readonly hideDescription = this.env.PURCHASE_FLOW?.HIDE_PRODUCT_CATEGORY_DESCRIPTION;
    public periodTypesList: PeriodType[] = [];
    public priceForBooklet: number;
    public bookletCurrency: string;
    currency = this.appStartupService.getCurrency();

    constructor(
        private applicationFacade: ApplicationFacade,
        private purchaseService: PurchaseService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        public translate: TranslateService,
        public appStartupService: AppStartupService,
        private modalCtrl: ModalController,
        public ticketDurationDescriptionService: TicketDurationDescriptionService,
        @Inject('ENV') public env: Env,
        private translatePipe: TranslateObjectFieldPipe,
        private moneyFormatPipe: MoneyFormatPipe,
        private notification: NotificationFacade
    ) {
        super();
    }

    ngOnInit() {
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
            this.generateProductLabels();
            this.generatePeriodProductLabels();
        });

        const categoryProducts = this.fromProductToCategoryProduct(this.categoryProducts);

        this.productsGroups = this.groupByCode(categoryProducts);

        this.generateProductLabels();

        const productIdFromUrl: string = this.purchaseService.getStepDataIdsByStepName('category-product')[0];
        if (productIdFromUrl) {
            const selectedCategoryProduct = categoryProducts.find((p) => p.id === productIdFromUrl);
            if (selectedCategoryProduct) {
                this.selectedProductId = selectedCategoryProduct.id;
            }
        }

        if (this.env.IS_NEW_AFC) {
            this.productTypeSelectionsIsLoading = true;
            this.purchaseService
                .fetchPeriodTypes()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (data: PeriodType[]) => {
                        this.periodTypesList = data;
                        this.generatePeriodProductLabels();
                        this.productTypeSelectionsIsLoading = false;
                    },
                    (e: Omit<HttpErrorResponse, 'error'> & { error: WmbApiErrorResponse }) => {
                        this.productTypeSelectionsIsLoading = false;
                        this.notification.error(this.translate.instant(e.error?.code || 'INTERNAL_ERROR'));
                    }
                );
        }

        if (this.env.PURCHASE_FLOW?.BOOKLET_JP_ENABLE) {
            this.getPriceAndCurrencyForBooklet();
        }

        this.purchaseService.setPurchaseDataLoading(false);
    }

    public async selectProductGroup(productGroupSelection: string, pType?: string) {
        const urlSegments = this.activatedRoute.snapshot.url.map((segment) => segment.path);
        if (this.selectedProductId) {
            urlSegments.pop();
        }

        this.selectedProductId = productGroupSelection;
        await this.router.navigate([
            this.translate.currentLang,
            ...this.urls,
            ...urlSegments,
            this.selectedProductId,
            pType ?? ''
        ]);
        this.nextStep.emit();
    }
    private generateProductLabels(): void {
        if (!this.productsGroups) return;

        this.productsGroups.forEach((group) => {
            if (
                group.code === ProductCode.SINGLE_TICKET ||
                group.code === ProductCode.SINGLE_TICKET_GROUP ||
                group.code === ProductCode.MULTI_TRIP
            ) {
                group.products?.forEach((product: CategoryProduct & { ariaLabel?: string }) => {
                    product.ariaLabel = this.getStandardProductAriaLabel(product);
                });
            }
        });
    }

    private generatePeriodProductLabels(): void {
        if (!this.periodTypesList || this.periodTypesList.length === 0) {
            return;
        }

        this.periodTypesList.forEach((period: PeriodType & { ariaLabel?: string }) => {
            period.ariaLabel = this.getPeriodAriaLabel(period);
        });
    }

    private getStandardProductAriaLabel(product: CategoryProduct): string {
        const name = this.translatePipe.transform(product.name, 'translation', this.currentLanguage);

        let priceString = '';
        if (product?.price) {
            let priceVal = '';
            if (product.price.amount) {
                priceVal = this.moneyFormatPipe.transform(product.price.amount);
            } else if (product.price.minAmount) {
                const min = this.moneyFormatPipe.transform(product.price.minAmount);
                const max = this.moneyFormatPipe.transform(product.price.maxAmount);
                priceVal = `${min} - ${max}`;
            }

            if (priceVal) {
                priceString = `, ${priceVal} ${product.price.currency || this.currency}`;
            }
        }

        return `${name}${priceString}`;
    }

    private getPeriodAriaLabel(period: PeriodType): string {
        const name = this.translatePipe.transform(period.name, 'translation', this.currentLanguage);
        const price = this.moneyFormatPipe.transform(this.getPriceByTimeInterval(period.code));
        const currency = this.getCurrencyByTimeInterval(period.code);

        return `${name}, ${price} ${currency}`;
    }

    public getPriceByTimeInterval(str: string) {
        const filterProducts = this.getFilteredProductsByPeriodCode(str);

        return filterProducts?.price.amount || filterProducts?.price.maxAmount || 0;
    }

    public getPriceAndCurrencyForBooklet(): void {
        const group = this.productsGroups.find((group) => group.code.includes('booklet'));
        const groupProducts = group?.products[0].products;

        if (groupProducts?.length) {
            this.bookletCurrency = groupProducts[0].price.currency ?? this.currency;
            this.priceForBooklet = groupProducts.reduce(
                (prev, next) => (prev >= next.price?.maxAmount ? prev : next.price?.maxAmount),
                0
            );
        }
    }

    public isShowPeriodTypeItem(productGroup: CategoryProduct, code: string): boolean {
        if (!this.env.PURCHASE_FLOW?.SORTING_BY_CODE) {
            return true;
        }

        return productGroup.products.some((product) =>
            product.usageValidityPeriods.some((validityPeriod) => validityPeriod.code === code)
        );
    }

    public getCurrencyByTimeInterval(str: string) {
        const filterProducts = this.getFilteredProductsByPeriodCode(str);

        return filterProducts?.price.currency || this.currency;
    }

    private groupByCode(categoryProducts: CategoryProduct[]): ProductGroup[] {
        const groupedProduct = Object.values(
            categoryProducts.reduce((result, { code, ...item }) => {
                (result[code] = result[code] || { code: code, products: [] }).products.push(item);
                return result;
            }, {})
        ) as ProductGroup[];

        const singleTicketGroupIndex = groupedProduct.findIndex((group) => group.code === 'SINGLE');
        if (singleTicketGroupIndex !== -1) {
            const singleTicketGroup = groupedProduct[singleTicketGroupIndex];
            const reordered = [
                singleTicketGroup,
                ...groupedProduct.slice(0, singleTicketGroupIndex),
                ...groupedProduct.slice(singleTicketGroupIndex + 1)
            ];

            const withoutSingle = reordered.slice(1);
            const multiIndex = withoutSingle.findIndex((g) => g.code === 'MULTI_JOURNEY');
            if (multiIndex !== -1) {
                const multiGroup = withoutSingle[multiIndex];
                const others = withoutSingle.filter((g) => g.code !== 'MULTI_JOURNEY');
                return [singleTicketGroup, ...others, multiGroup];
            }

            return reordered;
        }

        return groupedProduct;
    }

    private fromProductToCategoryProduct(categoryProducts: CategoryProduct[]): CategoryProduct[] {
        return categoryProducts?.map((product: CategoryProduct) => {
            return {
                ...product,
                price: product.products.map((p) => p.price).reduce((max, p) => (p?.amount > p?.amount ? p : max))
            } as CategoryProduct;
        });
    }

    async presentModal(productGroupSelection: string, productCode: ProductCode) {
        const isSingleTicketGroup = productCode === ProductCode.SINGLE_TICKET_GROUP;
        const productCodeInUrlSegment = isSingleTicketGroup ? 'productType:SINGLE_TICKET_GROUP' : productGroupSelection;
        const productTypeInUrlSegment = isSingleTicketGroup ? productGroupSelection : undefined;

        const product = this.getProduct(productCodeInUrlSegment, productCode);

        // A multi-zone ticket stays valid for 3h, a single-zone one for 1h. Only warn when the
        // planned journey outlasts the ticket the user is about to buy.
        const journeyFitsMultiZoneTicket = this.duration < 3 && this.zones > 1;
        const shouldWarnAboutExpiry = !journeyFitsMultiZoneTicket && this.duration > 1 && !!product?.id;

        if (shouldWarnAboutExpiry) {
            const modal = await this.modalCtrl.create({
                component: TicketWarningModalComponent,
                cssClass: 'ticket-warning-modal',
                componentProps: {
                    duration: this.duration
                }
            });
            await modal.present();
            await modal.onDidDismiss();
        }

        this.selectProductGroup(productCodeInUrlSegment, productTypeInUrlSegment);
    }

    private getProduct(productId: string, productCode: ProductCode): CategoryProduct {
        const isMultiTicketWarningEnabled = this.env.PURCHASE_FLOW?.MULTI_TICKET_WARNING;
        const group = this.productsGroups.find((group) => {
            if (isMultiTicketWarningEnabled) {
                return productCode === group.code && group.code !== ProductCode.PERIOD;
            } else {
                return group.code === ProductCode.SINGLE;
            }
        });

        return group?.products.find((product) => product.id === productId);
    }

    private getFilteredProductsByPeriodCode(str: string): Product | undefined {
        const productGroup = this.productsGroups.find((group) => group.code.toLowerCase().includes('period'));

        if (!productGroup?.products?.length) {
            return null;
        }
        const groupProducts: Product[] = productGroup.products?.[0]?.products;

        return groupProducts?.find((item: Product) => item.usageValidityPeriods?.some((uvp) => uvp.code === str));
    }

    public isStringName(name: string | { translations: Array<any> }): boolean {
        return typeof name === 'string';
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
